/**
 * MapZoomZone Component
 * Renders a zoom zone marker with icon and label
 */

import React, { useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useHover, useWrappedTextWidth } from '../MapLabels/MapLabelsHooks';
import {
  useConfigStore, useMapInteractionStore, useMapDataStore, useMapStateStore,
} from '../../../stores';
import './MapZoomZone.scss';

/**
 * Map Zoom Zone Component - clickable zoom icon with label
 */
const MapZoomZone = ({
  zoneKey, zoomZone, position, onZoomZoneClick, isPulsing = false, isOnFloor = false,
  labelVisible = true, onToggleLabel, orientationOverride, iconOffset,
}) => {
  const [isHovered, { onMouseEnter, onMouseLeave }] = useHover();
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowZoomZonesLevelsOfCare } = featureFlags || {};
  const activeLevelOfCareUuid = useMapInteractionStore((state) => state.activeLevelOfCareUuid);
  const setActiveLevelOfCareUuid = useMapInteractionStore(
    (state) => state.setActiveLevelOfCareUuid,
  );
  const setActiveLevelSvgId = useMapStateStore((state) => state.setActiveLevelSvgId);
  const {
    name,
    orientation: baseOrientation = 'bottom',
    has_active_content: hasActiveContent = false,
    level_of_care: zoneLocs,
    // `wrap_label` mirrors field_wrap_pin_label on the map_building_grouping
    // term: TRUE by default, so only an explicit FALSE turns wrapping off and
    // an older payload with no flag keeps wrapping.
    wrap_label: wrapLabel = true,
  } = zoomZone;
  const orientation = orientationOverride || baseOrientation;

  // Sort LoCs by weight ascending (matches pickPrimaryLevelOfCareUuid). On-floor
  // zones with 2+ LoCs render each name as an inline clickable span (inactive
  // names are underlined). Bird's-eye and single-LoC zones render the same
  // joined text non-interactively. Single-LoC subtitles are suppressed when
  // the LoC name already appears in the zone name (e.g. "Rocamadour Skilled
  // Nursing" + "Skilled Nursing" — redundant).
  const sortedLocs = (() => {
    if (!platformShowZoomZonesLevelsOfCare) return [];
    if (!Array.isArray(zoneLocs) || zoneLocs.length === 0) return [];
    return [...zoneLocs]
      .filter((l) => l?.name && l?.uuid)
      .sort((a, b) => {
        const aw = parseInt(a?.weight, 10);
        const bw = parseInt(b?.weight, 10);
        return (Number.isNaN(aw) ? Infinity : aw) - (Number.isNaN(bw) ? Infinity : bw);
      });
  })();
  const showLocLinks = isOnFloor && sortedLocs.length >= 2;
  const locDisplayText = (() => {
    if (sortedLocs.length === 0) return null;
    const sortedNames = sortedLocs.map((l) => l.name);
    if (sortedNames.length === 1) {
      const single = sortedNames[0];
      const nameLower = name ? name.toLowerCase() : '';
      return nameLower.includes(single.toLowerCase()) ? null : single;
    }
    if (sortedNames.length === 2) return `${sortedNames[0]} & ${sortedNames[1]}`;
    return `${sortedNames.slice(0, -1).join(', ')} & ${sortedNames[sortedNames.length - 1]}`;
  })();
  const showLoc = !!locDisplayText;

  // Underline toggle has no layout impact, so the active-state doesn't need to
  // be in the signature — only structural fields (name + joined subtitle).
  const measureSignature = [name, locDisplayText || ''].join('|');
  const { labelRef, textRef, fittedWidth } = useWrappedTextWidth(measureSignature, wrapLabel);

  // `--asDot` zones (location has active content) toggle their label like
  // non-link place labels — click hides/shows the label without navigating.
  // All other zones navigate on click.
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (hasActiveContent) {
      onToggleLabel?.();
    } else {
      onZoomZoneClick?.(zoomZone);
    }
  }, [hasActiveContent, zoomZone, onZoomZoneClick, onToggleLabel]);

  // LoC name click: set active LoC, and if THIS zone has no content on the
  // current level for the clicked LoC, jump to the first level where it does.
  // The check is zone-scoped (matches by map_building_grouping uuid) because
  // the user is navigating from this specific zone — a different building's
  // place tagged with the same LoC isn't what they're asking for. Walks both
  // places (level_of_care is an array of LoC objects) and units
  // (info.level_of_care is a scalar LoC object). svgData / activeLevelSvgId
  // are read via getState() rather than subscribed — values are only needed
  // at click time, and every rendered zone would otherwise subscribe.
  const zoneUuid = zoomZone?.uuid;
  const handleLocLinkClick = useCallback((e, locUuid) => {
    e.stopPropagation();
    if (!locUuid) return;
    if (locUuid === activeLevelOfCareUuid) return;
    setActiveLevelOfCareUuid(locUuid);

    if (!zoneUuid) return;
    const { svgData } = useMapDataStore.getState();
    const { activeLevelSvgId } = useMapStateStore.getState();
    if (!svgData?.levels || !activeLevelSvgId) return;

    const levelHasMatch = (level) => {
      if (!level) return false;
      const placeMatch = Object.values(level.places || {}).some((p) => {
        if (p?.map_building_grouping?.uuid !== zoneUuid) return false;
        const locs = p?.level_of_care;
        return Array.isArray(locs) && locs.some((loc) => loc?.uuid === locUuid);
      });
      if (placeMatch) return true;
      return Object.values(level.units || {}).some((u) => {
        if (u?.info?.map_building_grouping?.uuid !== zoneUuid) return false;
        return u?.info?.level_of_care?.uuid === locUuid;
      });
    };

    if (levelHasMatch(svgData.levels[activeLevelSvgId])) return;
    const targetLevelId = Object.keys(svgData.levels)
      .find((id) => levelHasMatch(svgData.levels[id]));
    if (targetLevelId && targetLevelId !== activeLevelSvgId) {
      setActiveLevelSvgId(targetLevelId);
    }
  }, [activeLevelOfCareUuid, setActiveLevelOfCareUuid, setActiveLevelSvgId, zoneUuid]);

  const labelStyle = fittedWidth ? { width: `${fittedWidth}px` } : undefined;
  const showLabel = name && (!hasActiveContent || labelVisible);

  return (
    <div
      className={classNames(
        'htmlZoomZone',
        `htmlZoomZone--${orientation}`,
        {
          'htmlZoomZone--hovered': isHovered,
          'htmlZoomZone--pulsing': isPulsing,
          'htmlZoomZone--onFloor': isOnFloor,
          'htmlZoomZone--asDot': hasActiveContent,
          'htmlZoomZone--noWrap': !wrapLabel,
        },
      )}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        // Compose the offset with the -50%/-50% centering from
        // %html-element-base. Setting `transform: translate(Npx, Npx)` alone
        // would clobber the centering and shift the wrapper by half its
        // size + the intended offset — which back-out math in the collision
        // pass couldn't account for, causing oscillation.
        ...(iconOffset && {
          transform: `translate(calc(-50% + ${iconOffset.dx}px), calc(-50% + ${iconOffset.dy}px))`,
        }),
      }}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
      data-testid={`zoom-zone:${(name || '').toLowerCase().replace(/\s+/g, '-')}`}
      data-zone-key={zoneKey}
      data-zone-orientation={baseOrientation}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {hasActiveContent ? (
        <div className="htmlZoomZone__dot">
          <span className="htmlZoomZone__dot__plus" />
        </div>
      ) : (
        <div className="htmlZoomZone__icon">
          <div className="htmlZoomZone__icon__bg" />
          <div className="htmlZoomZone__icon__doughnut" />
          {isOnFloor ? (
            <svg
              className="htmlZoomZone__icon__arrow"
              viewBox="0 0 16 16"
              aria-hidden="true"
            >
              <path
                d="M5 11 L11 5 M6.5 5 L11 5 L11 9.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <span className="htmlZoomZone__icon__plus" />
          )}
        </div>
      )}
      {showLabel && (
        <div className="htmlZoomZone__label" ref={labelRef} style={labelStyle}>
          <span className="htmlZoomZone__text" ref={textRef}>{name}</span>
          {showLoc && (
            <span className="htmlZoomZone__loc">
              {showLocLinks
                ? sortedLocs.map((loc, idx) => {
                  const isActive = loc.uuid === activeLevelOfCareUuid;
                  const isLast = idx === sortedLocs.length - 1;
                  const isPenultimate = idx === sortedLocs.length - 2;
                  const separator = isLast ? null : (isPenultimate ? ' & ' : ', ');
                  return (
                    <React.Fragment key={loc.uuid}>
                      <span
                        className={classNames('htmlZoomZone__locLink', {
                          'htmlZoomZone__locLink--active': isActive,
                        })}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleLocLinkClick(e, loc.uuid)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleLocLinkClick(e, loc.uuid);
                          }
                        }}
                      >
                        {loc.name}
                      </span>
                      {separator}
                    </React.Fragment>
                  );
                })
                : locDisplayText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

MapZoomZone.propTypes = {
  // Stable per-zone key, `zone:${uuid|tid|index}`. Also written to the DOM as
  // data-zone-key.
  zoneKey: PropTypes.string,
  // One entry of the map payload's birds_eye_view.zoom_zones, normalized by
  // normalizeZoomZones. Snake_case keys are Drupal's, kept verbatim.
  zoomZone: PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    orientation: PropTypes.string,
    has_active_content: PropTypes.bool,
    wrap_label: PropTypes.bool,
    default_level_svg_id: PropTypes.string,
    level_of_care: PropTypes.arrayOf(PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
      weight: PropTypes.number,
    })),
  }).isRequired,
  // Container-relative position, in percent.
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }).isRequired,
  onZoomZoneClick: PropTypes.func,
  isPulsing: PropTypes.bool,
  // True on a map level, false in bird's-eye. Drives the inverted palette,
  // the arrow glyph and the per-LoC links.
  isOnFloor: PropTypes.bool,
  // Only meaningful for `has_active_content` zones, which toggle their label
  // instead of navigating.
  labelVisible: PropTypes.bool,
  onToggleLabel: PropTypes.func,
  // Supplied by useZoneLabelCollision. Undefined when the pass is off (bird's
  // eye), or once it has frozen, in which case the zone uses its configured
  // orientation unshifted.
  orientationOverride: PropTypes.string,
  iconOffset: PropTypes.shape({
    dx: PropTypes.number,
    dy: PropTypes.number,
  }),
};

MapZoomZone.defaultProps = {
  zoneKey: undefined,
  onZoomZoneClick: undefined,
  isPulsing: false,
  isOnFloor: false,
  labelVisible: true,
  onToggleLabel: undefined,
  orientationOverride: undefined,
  iconOffset: undefined,
};

export default MapZoomZone;
